import { DatePicker, Input, InputNumber, Select } from 'antd'
import {
  camelCase,
  clone,
  filter,
  find,
  has,
  includes,
  isEqual,
  keys,
  map,
  pick,
  size,
  startCase,
  values,
} from 'lodash'
import { REPORT_CONTROL_TYPES } from '@/screens/QSF/InspectionReport/InSpectionReport.constant'
import './InspectionReportDrawer.style.less'
import EditableChoiceListComponent from './EditableChoiceListComponent'
import { useEffect, useState } from 'react'
import { DATE_FORMAT } from '@/constant/date'
import moment from 'moment'
import EditPhotoComponent from './EditPhotoComponent'

const {
  MULTI_SELECT,
  SELECT,
  TEXT,
  NUMBER,
  DATE,
  EDITABLE_ENTRY_LIST,
  SELECT_DROPDOWN,
  PHOTO,
} = REPORT_CONTROL_TYPES
const { DDMMYY } = DATE_FORMAT

interface GroupCheckpointComponentProps {
  entryIndex?: number
  photoSelected?: any
  addNewPhotoKey?: string
  checkpointKey?: string
  checkpoints?: Array<any>
  value?: any // Add value prop
  id?: string
  onChange?: (value: any) => void // Add onChange prop
}

interface ItemDataType {
  name: string
  value: any
  control_type: string
}

function GroupCheckpointComponent(props: GroupCheckpointComponentProps) {
  const [stateData, setStateData] = useState([])
  const [statePhotoSelected, setStatePhotoSelected] = useState(null)
  const [stateAddNewPhotoKey, setStateAddNewPhotoKey] = useState('')
  const [stateCheckpointKey, setStateCheckpointKey] = useState('')
  const [preparePayload, setPreparePayload] = useState([])

  const handleOnChangeState = (name, value, options: any = {}) => {
    const cloneDef: any = clone(preparePayload)
    const { control_type = '', photoIndex } = options

    if (includes([PHOTO], control_type)) {
      cloneDef.photoExtraOption = { ...value, checkpointName: name }
      props.onChange(cloneDef)
    } else {
      if (!isEqual(preparePayload[`${name}`].value, value)) {
        cloneDef[`${name}`].value = value
      }
      props.onChange(cloneDef)
    }
  }

  const renderItemText = (dataText: ItemDataType) => {
    const { name, value } = dataText
    const title = value?.title || ''
    const displayLabel = <p>{startCase(camelCase(title || name))}</p>

    return (
      <div className="item-entry">
        {displayLabel}
        <Input
          size="large"
          placeholder="Text"
          defaultValue={value.value}
          onChange={(v) => {
            handleOnChangeState(name, v.target.value)
          }}
        />
      </div>
    )
  }

  const renderItemDate = (data: ItemDataType) => {
    const { name, value } = data
    const title = value?.title || ''
    const cvDate = value?.value ? moment.utc(value?.value) : null
    const displayLabel = <p>{startCase(camelCase(title || name))}</p>

    return (
      <div className="item-entry">
        {displayLabel}
        <DatePicker
          defaultValue={cvDate}
          style={{ marginBottom: 8 }}
          format={DDMMYY}
          size="large"
          onChange={(v) => {
            const value = v?.toISOString() ?? null
            handleOnChangeState(name, value)
          }}
        />
      </div>
    )
  }

  const renderNumber = (dataNumber: ItemDataType) => {
    const { name, value } = dataNumber
    const title = value?.title || ''
    const displayLabel = <p>{startCase(camelCase(title || name))}</p>

    return (
      <div className="item-entry">
        {displayLabel}
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Number"
          size="large"
          onWheel={(e: any) => e.target.blur()}
          formatter={(value) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          }
          controls={false}
          defaultValue={value.value}
          onChange={(v) => {
            handleOnChangeState(name, v)
          }}
        />
      </div>
    )
  }

  const renderItemSelect = (data: ItemDataType) => {
    const { name, value } = data
    const title = value?.title || ''
    const displayLabel = <p>{startCase(camelCase(title || name))}</p>
    const defaultValue = size(value.value) ? value.value : null

    return (
      <div className="item-entry">
        {displayLabel}
        <Select
          value={defaultValue}
          allowClear
          placeholder="Result"
          options={value.data_source}
          labelInValue
          onChange={(v = null) => {
            handleOnChangeState(name, v)
          }}
        />
      </div>
    )
  }

  const renderItemMultipleSelect = (data: ItemDataType) => {
    const { name, value } = data
    const title = value?.title || ''
    const displayLabel = <p>{startCase(camelCase(title || name))}</p>

    return (
      <div className="item-entry">
        {displayLabel}
        <Select
          defaultValue={value.value}
          mode="multiple"
          allowClear
          size="large"
          placeholder="Please select"
          options={value.data_source}
          labelInValue
          onChange={(v) => {
            handleOnChangeState(name, v)
          }}
        />
      </div>
    )
  }

  const renderItemPhoto = (data: ItemDataType) => {
    const { name, control_type, value } = data
    const getLastLength = size(value.value) // getLastLength when create new photo only
    return (
      <div>
        <EditPhotoComponent
          {...data}
          photoSelected={statePhotoSelected}
          addNewPhotoKey={stateAddNewPhotoKey}
          onChange={(v) => {
            handleOnChangeState(name, v, {
              control_type,
              photoIndex: statePhotoSelected?.key || getLastLength,
            })
          }}
        />
      </div>
    )
  }

  // When data fetching
  useEffect(() => {
    if (size(props.value)) {
      const wrapOjb = map(props.value, (item, key) => {
        // Find title from checkpoint by initial name of the template
        const titleCheckpoint = find(props.checkpoints, { name: key })
        const mapItem = { ...item, title: titleCheckpoint?.title || key }
        return { [key]: mapItem }
      })

      setStateData(wrapOjb)
      setStatePhotoSelected(props.photoSelected)
      setStateAddNewPhotoKey(props.addNewPhotoKey)
      setStateCheckpointKey(props.checkpointKey)
      setPreparePayload(props.value)
    }
  }, props.value)

  const handleRenderFormItem = (dataItem: any) => {
    const getKey = keys(dataItem)
    const breakObject = values(dataItem)[0]
    const { control_type } = breakObject
    const wrapData = {
      name: getKey[0],
      value: breakObject,
      control_type,
    }

    const isSelectedCheckpoint = stateCheckpointKey
      ? wrapData.name === stateCheckpointKey
      : true

    if (!!size(statePhotoSelected)) {
      if (includes([PHOTO], control_type) && isSelectedCheckpoint) {
        return renderItemPhoto(wrapData)
      }
    } else if (stateAddNewPhotoKey) {
      if (includes([PHOTO], control_type) && isSelectedCheckpoint) {
        return renderItemPhoto(wrapData)
      }
    } else {
      if (includes([TEXT], control_type)) {
        return renderItemText(wrapData)
      }

      if (includes([NUMBER], control_type)) {
        return renderNumber(wrapData)
      }

      if (includes([DATE], control_type)) {
        return renderItemDate(wrapData)
      }

      if (includes([SELECT, SELECT_DROPDOWN], control_type)) {
        return renderItemSelect(wrapData)
      }

      if (includes([MULTI_SELECT], control_type)) {
        return renderItemMultipleSelect(wrapData)
      }

      if (includes([EDITABLE_ENTRY_LIST], control_type)) {
        const displayLabel = <p>{startCase(camelCase(wrapData.name))}</p>
        return (
          <div className="item-entry">
            <div className="editable-choice-container">
              {displayLabel}
              <EditableChoiceListComponent
                value={wrapData.value.value}
                dataSource={wrapData.value.data_source}
                onChange={(values) => {
                  const result = filter(values, Boolean)

                  handleOnChangeState(wrapData.name, result)
                }}
              />
            </div>
          </div>
        )
      }
    }
    return <></>
  }

  return (
    <div className="group-checkpoint-container">
      <p>Entry #{props.entryIndex + 1}</p>
      {map(stateData, (item, itemKey) => (
        <div key={itemKey}>{handleRenderFormItem(item)}</div>
      ))}
    </div>
  )
}
export default GroupCheckpointComponent
